"use client";

import { ArrowLeft } from "lucide-react";
import { useEffect, useRef } from "react";

import { NavPanelShell } from "@/components/nav/NavPanel";
import NavSearchResults from "@/components/nav/NavSearchResults";
import { SEARCH_TILE } from "@/components/nav/NavSearchTrigger";
import { NAV_SCROLLBAR } from "@/components/nav/nav-styles";
import { Kbd } from "@/components/ui/kbd";
import { cn } from "@/lib/utils";

// The search row's field, now the input: the same white rounded field, its
// border coral while it is live, the back arrow in the icon's place.
const LIVE_FIELD =
  "h-9 gap-2 rounded-xl border border-brand-600 bg-white pl-1 pr-3";

const HINTS = [
  ["↑↓", "move"],
  ["↵", "open"],
  ["esc", "close"],
];

/**
 * Search beside the icon rail: the input stays in the nav's search row (the
 * field the person clicked becomes it, with a back arrow where its icon was;
 * `inputSlot` is that row's element) and the results open in a pane in the
 * panel's place, wider than a menu, with the keys that drive them at its
 * foot. It opens from the field, ⌘K, or typing at the nav, and closes on
 * Escape, the back arrow, or choosing a result; the menu the panel showed
 * comes back as it was. Escape and the arrow hand focus back to the field.
 */
export default function NavSearchPane({ search, open, apple, inputSlot }) {
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) search.focusInput(inputRef.current);
  }, [open]);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      search.dismiss();
    }
  };

  return (
    <NavPanelShell open={open} width="w-80" label="Search">
      <NavSearchResults
        search={search}
        inputRef={inputRef}
        apple={apple}
        onKeyDown={onKeyDown}
        inputSlot={inputSlot}
        inputWrapperClassName={LIVE_FIELD}
        inputClassName="h-full py-0"
        inputLeading={
          <button
            type="button"
            aria-label="Close search"
            onClick={search.dismiss}
            className={cn("h-7 w-7 rounded-lg", SEARCH_TILE)}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        }
        listClassName={`max-h-none flex-1 px-1 pb-2 ${NAV_SCROLLBAR}`}
      />
      <div className="flex shrink-0 gap-3 border-t border-stone-200/70 px-4 py-2 text-[11px] text-gray-500">
        {HINTS.map(([key, action]) => (
          <span key={action} className="flex items-center gap-1">
            <Kbd>{key}</Kbd>
            {action}
          </span>
        ))}
      </div>
    </NavPanelShell>
  );
}
